import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { ManagedBackupCreation, ManagedBackupDownload, ManagedBackupList } from '../types';

const DEFAULT_PAGE_SIZE = 25;

export async function listManagedBackups(customerId: string, cursor?: string): Promise<ManagedBackupList> {
  const call = httpsCallable<{ customerId: string; limit: number; cursor?: string }, ManagedBackupList>(functions, 'listManagedBackups');
  const result = await call({ customerId, limit: DEFAULT_PAGE_SIZE, ...(cursor ? { cursor } : {}) });
  return result.data;
}

export async function createManagedBackup(customerId: string): Promise<ManagedBackupCreation> {
  const call = httpsCallable<{ customerId: string }, ManagedBackupCreation>(functions, 'createManagedBackup');
  const result = await call({ customerId });
  return result.data;
}

export async function downloadManagedBackup(customerId: string, backupId: string): Promise<ManagedBackupDownload> {
  const call = httpsCallable<{ customerId: string; backupId: string }, ManagedBackupDownload>(functions, 'downloadManagedBackup');
  const result = await call({ customerId, backupId });
  return result.data;
}

export function formatBackupSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / (1024 ** unitIndex);
  const digits = unitIndex === 0 || value >= 10 ? 0 : 1;
  return `${value.toFixed(digits)} ${units[unitIndex]}`;
}

export function countBackupRecords(docCounts: Record<string, number>): number {
  return Object.values(docCounts).reduce((total, value) => total + (Number.isFinite(value) && value > 0 ? value : 0), 0);
}

export function formatBackupTrigger(trigger: string): string {
  if (trigger === 'scheduled') return 'Scheduled';
  if (trigger === 'managed') return 'Manual';
  if (trigger === 'manual') return 'Legacy / Compass App';
  return 'Unknown';
}

export function backupActor(trigger: string, createdBy: string | null): string {
  return trigger === 'scheduled' ? 'System' : createdBy || 'Not recorded';
}

export function formatBackupDate(value: string | null): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return 'Not recorded';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function backupErrorMessage(error: unknown, operation: 'list' | 'create' | 'download'): string {
  const rawCode = typeof error === 'object' && error && 'code' in error ? String(error.code) : '';
  const code = rawCode.replace(/^functions\//, '');
  if (code === 'unauthenticated') return 'Your session has expired. Sign in again to manage backups.';
  if (code === 'permission-denied') return 'You do not have permission to manage backups.';
  if (operation === 'download' && code === 'not-found') return 'This backup is no longer available.';
  if (operation === 'list') return 'Backups could not be loaded. Please try again.';
  if (operation === 'create') return 'Backup creation failed. Please try again.';
  return 'Backup download failed. Please try again.';
}

function safeFilename(filename: string): string {
  return filename.split(/[\\/]/).pop()?.trim() || 'compass-backup.zip';
}

export function startBackupDownload(payload: ManagedBackupDownload): void {
  const anchor = document.createElement('a');
  anchor.download = safeFilename(payload.filename);
  anchor.rel = 'noopener noreferrer';

  if ('zipBase64' in payload && payload.zipBase64) {
    const binary = window.atob(payload.zipBase64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/zip' }));
    anchor.href = objectUrl;
    anchor.click();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    return;
  }

  if (!payload.downloadUrl) throw new Error('Backup response did not include downloadable content.');
  anchor.href = payload.downloadUrl;
  anchor.target = '_blank';
  anchor.click();
}
